import { useState, type ReactNode } from 'react';
import styles from './BloomCalendar.module.css';

export type BloomStatus = 'blooming' | 'soon' | 'later' | 'over';

export interface BloomCalendarItem {
  key: string;
  /** Name of the plant, e.g. a link. */
  label: ReactNode;
  /** Visible details next to the name, e.g. dates and a status badge. */
  meta?: ReactNode;
  /** This year's period as ISO dates (`YYYY-MM-DD`) of `year`. */
  start: string;
  end: string;
  /** The usual period if this year's is shifted, shown as an outline. */
  usualStart?: string | undefined;
  usualEnd?: string | undefined;
  status: BloomStatus;
  /** Shown on hover or focus of the bar. */
  tooltip?: ReactNode;
}

export interface BloomCalendarProps {
  /** Accessible name of the list. */
  label: string;
  year: number;
  /** Today as ISO date; marked if it lies in `year`. */
  today: string;
  /** Twelve short month labels, e.g. "J", "F", … */
  months: string[];
  /** Label of the today marker. */
  todayLabel: string;
  items: BloomCalendarItem[];
}

function dayOfYear(iso: string, year: number): number {
  const [y, m, d] = iso.split('-').map(Number);
  const start = Date.UTC(year, 0, 1);
  const date = Date.UTC(y ?? year, (m ?? 1) - 1, d ?? 1);
  return Math.round((date - start) / 86_400_000);
}

function daysIn(year: number): number {
  return dayOfYear(`${year + 1}-01-01`, year);
}

/**
 * A year as a timeline with one bar per plant (forage calendar, TRA-003):
 * months as columns, a marker for today, the bars coloured by status.
 * Names, dates and status stay visible as text, so the bars are decorative
 * for screen readers.
 */
export function BloomCalendar({
  label,
  year,
  today,
  months,
  todayLabel,
  items,
}: BloomCalendarProps) {
  const [active, setActive] = useState<string | null>(null);
  const total = daysIn(year);
  const pct = (iso: string) => (Math.min(Math.max(dayOfYear(iso, year), 0), total) / total) * 100;
  const span = (start: string, end: string) => {
    const left = pct(start);
    // The end day counts: a bar from 1 to 1 May is one day wide.
    const right = Math.min(100, ((dayOfYear(end, year) + 1) / total) * 100);
    return { left: `${left}%`, width: `${Math.max(right - left, 0.6)}%` };
  };
  const monthStarts = months.map((_, i) => pct(`${year}-${String(i + 1).padStart(2, '0')}-01`));
  const todayIn = today.startsWith(`${year}-`);
  const todayAt = todayIn ? `${pct(today)}%` : undefined;

  const guides = (
    <>
      {monthStarts.slice(1).map((left, i) => (
        <span key={i} className={styles.month} style={{ left: `${left}%` }} />
      ))}
      {todayAt && <span className={styles.today} style={{ left: todayAt }} />}
    </>
  );

  return (
    <div className={styles.calendar}>
      <div className={styles.header} aria-hidden="true">
        <span />
        <div className={styles.track}>
          {months.map((m, i) => (
            <span
              key={i}
              className={styles.monthLabel}
              style={{
                left: `${monthStarts[i]}%`,
                width: `${(monthStarts[i + 1] ?? 100) - (monthStarts[i] ?? 0)}%`,
              }}
            >
              {m}
            </span>
          ))}
          {todayAt && (
            <span className={styles.todayLabel} style={{ left: todayAt }}>
              {todayLabel}
            </span>
          )}
        </div>
      </div>
      <ul className={styles.rows} aria-label={label}>
        {items.map((item) => (
          <li key={item.key} className={styles.row} data-status={item.status}>
            <div className={styles.label}>
              <span className={styles.name}>{item.label}</span>
              {item.meta && <span className={styles.meta}>{item.meta}</span>}
            </div>
            <div
              className={styles.track}
              aria-hidden="true"
              onMouseEnter={() => setActive(item.key)}
              onMouseLeave={() => setActive(null)}
            >
              {guides}
              {item.usualStart && item.usualEnd && (
                <span className={styles.usual} style={span(item.usualStart, item.usualEnd)} />
              )}
              <span className={styles.bar} style={span(item.start, item.end)} />
              {active === item.key && item.tooltip && (
                <span
                  className={styles.tooltip}
                  style={{ left: `${Math.min(Math.max(pct(item.start), 8), 72)}%` }}
                >
                  {item.tooltip}
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
